/**
 * PATH: packages/email-hub/src/feature/email-layout/LayoutControl.tsx
 * FILE_ID: EMAIL-LAYOUT-CONTROL-001
 * PURPOSE: Preserve the portalled workspace-layout control.
 */

import {
  AdaptiveOverlay,
} from '@quotech/ui'
import {
  LayoutGrid,
  PanelBottom,
  PanelLeft,
  PanelRight,
  RotateCcw,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { cn } from '../../_internal/lib/utils'
import {
  useUiStore,
  type EmailNavPosition,
} from '../../_internal/stores/uiStore'

interface LayoutControlProps {
  variant?: 'rail' | 'bar'
  showLabel?: boolean
}

export function LayoutControl({ showLabel = false }: LayoutControlProps) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const navPosition = useUiStore(state => state.navPosition)
  const inboxSide = useUiStore(state => state.inboxSide)
  const setNavPosition = useUiStore(state => state.setNavPosition)
  const setInboxSide = useUiStore(state => state.setInboxSide)
  const resetLayout = useUiStore(state => state.resetLayout)

  const navOptions: Array<{
    id: EmailNavPosition
    label: string
    Icon: typeof PanelLeft
  }> = [
    { id: 'left', label: 'Left', Icon: PanelLeft },
    { id: 'right', label: 'Right', Icon: PanelRight },
    { id: 'bottom', label: 'Bottom', Icon: PanelBottom },
  ]

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(value => !value)}
        className={cn(
          'flex items-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors',
          showLabel ? 'gap-1.5 px-2 py-1.5 text-xs' : 'justify-center h-8 w-8',
          open && 'bg-accent text-foreground',
        )}
        title="Workspace layout - dock nav, move list, resize"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <LayoutGrid className="w-3.5 h-3.5 shrink-0" />
        {showLabel && <span>Layout</span>}
      </button>
      <AdaptiveOverlay
        open={open}
        onOpenChange={setOpen}
        anchorRef={btnRef}
        mode="auto"
        autoSheetBelowPx={640}
        label="Workspace layout"
        popoverWidth={208}
        testId="email-layout-menu"
      >
        <div
          id="email-layout-menu"
          className="w-full"
        >
          <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Navigation
          </p>
          <div className="grid grid-cols-3 gap-1">
            {navOptions.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                aria-pressed={navPosition === id}
                onClick={() => setNavPosition(id)}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-md border px-1 py-1.5 text-[10px] transition-colors',
                  navPosition === id
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border hover:bg-accent text-muted-foreground',
                )}
                title={`Dock navigation ${label.toLowerCase()}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
          <p className="px-1 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Message list
          </p>
          <div className="grid grid-cols-2 gap-1">
            {(['left', 'right'] as const).map(side => (
              <button
                key={side}
                type="button"
                aria-pressed={inboxSide === side}
                onClick={() => setInboxSide(side)}
                className={cn(
                  'flex items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-[10px] capitalize transition-colors',
                  inboxSide === side
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border hover:bg-accent text-muted-foreground',
                )}
                title={`Message list on the ${side}`}
              >
                {side === 'left'
                  ? <PanelLeft className="h-3 w-3" />
                  : <PanelRight className="h-3 w-3" />}
                {side}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              resetLayout()
              setOpen(false)
            }}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Restore the default layout"
          >
            <RotateCcw className="h-3 w-3" /> Reset layout
          </button>
        </div>
      </AdaptiveOverlay>
    </>
  )
}
